import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';

interface ModuleSection {
  title: string;
  content: string[];
}


const moduleContent: ModuleSection[] = [
  {
    title: 'Introduction to Healthy Eating',
    content: [
      'Healthy eating means consuming a variety of foods that provide the nutrients your body needs to maintain health, feel good, and have energy. These nutrients include protein, carbohydrates, fat, water, vitamins, and minerals.',
      'A healthy diet helps protect against malnutrition and noncommunicable diseases (NCDs) such as diabetes, heart disease, stroke, and cancer.',
      // removed source
    ]
  },
  {
    title: 'Key Principles of Healthy Eating',
    content: [
      '• Eat a variety of foods, including plenty of fruits and vegetables.',
      '• Limit intake of sugars, salt, and saturated fats.',
      '• Choose whole grains over refined grains.',
      '• Stay hydrated and practice moderation.',
      // removed source
    ]
  },
  {
    title: 'Building a Balanced Plate',
    content: [
      'A balanced plate includes appropriate portions of vegetables, fruits, whole grains, and lean proteins.',
      'Visualize your plate: half fruits and vegetables, one quarter whole grains, one quarter lean protein.',
      'Include healthy fats like olive oil, nuts, and seeds.',
      // removed source
    ]
  },
  {
    title: 'Tips for Lifelong Healthy Eating',
    content: [
      '• Plan meals ahead to avoid unhealthy choices.',
      '• Read nutrition labels to make informed decisions.',
      '• Practice mindful eating: eat slowly and savor your food.',
      '• Make gradual changes for sustainable results.',
      // removed source
    ]
  }
];


const HealthyEatingModule1Page: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress, isCompleted } = useModuleProgress('healthyEatingM1');
  const totalSteps = moduleContent.length;
  const safeCurrentStep = Math.max(0, Math.min(currentStep, totalSteps - 1));
  const currentModule = moduleContent[safeCurrentStep];
  const progress = ((safeCurrentStep + 1) / totalSteps) * 100;


  const handleNext = async () => {
    if (currentStep < totalSteps - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      await saveProgress(nextStep, nextStep === totalSteps - 1);
    }
  };

  const handleBack = async () => {
    if (currentStep === 0) {
      navigate('/panel/wellness-loop/healthy-eating-habits');
      return;
    }
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    await saveProgress(prevStep, isCompleted);
  };

  const handleFinish = async () => {
    await saveProgress(currentStep, true);
    navigate('/panel/wellness-loop/healthy-eating-habits');
  };

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  if (!currentModule) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Error loading module content. Please try refreshing the page.</p>
      </div>
    );
  }

  const showCompletionMessage = safeCurrentStep === totalSteps - 1;

  return (
    <div className="module-form">
      <div className="module-container">
        {/* Left Panel - Illustration */}
        <div className="module-left nutritional-panel">
          <div className="module-header">
            <h1>Healthy Eating Module 1</h1>
            <p className="module-subtitle">Introduction to Healthy Eating</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Learn the basics of healthy eating, key principles, and how to build a nutritious diet for lifelong wellness.
            </p>
          </div>
        </div>

        {/* Right Panel - Content */}
        <div className="module-right">
          <div className="module-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Page {safeCurrentStep + 1} of {totalSteps}
              </span>
            </div>

            <div className="module-section">
              <h2 className="module-title">{currentModule.title}</h2>
              <div className="content-section">
                {currentModule.content.map((paragraph, index) => (
                  <p key={index} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}
              </div>
            </div>
          </div>

          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={safeCurrentStep === 0 ? handleFinish : handleBack}
            >
              {safeCurrentStep === 0 ? 'Exit Module' : 'Previous'}
            </button>
            {safeCurrentStep < totalSteps - 1 ? (
              <button
                className="btn-primary"
                onClick={handleNext}
              >
                Next
              </button>
            ) : (
              <button
                className="btn-primary"
                onClick={handleFinish}
              >
                Finish Module
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HealthyEatingModule1Page;

